import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Spin,
  message,
  Button,
  Table,
  Tag,
  Space,
  Select,
  Modal,
  Descriptions,
  Input,
  Tabs,
  Badge,
  Progress,
  Tooltip,
} from 'antd';
import {
  RadarChartOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  RocketOutlined,
  FileTextOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_SERVIDOR2 } from '../config/api';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

interface Produto {
  id: string;
  nome: string;
  descricao: string;
  total: number;
  cor: string;
  prioridade?: string;
  acao?: string;
  icone?: string;
}

interface Empresa {
  cnpj_completo: string;
  cnpj_basico: string;
  nome_devedor: string;
  valor_total: number;
  total_dividas: number;
  tipos_divida: string[];
  tem_fgts: boolean;
  tem_prev: boolean;
  tem_sida: boolean;
  qtd_ajuizadas: number;
  qtd_nao_ajuizadas: number;
  alguma_ajuizada: boolean;
  tempo_divida_mais_antiga_anos: number;
  uf: string;
  faixa_valor: string;
  faixa_tempo: string;
  score_risco: number;
  linha: string;
  score?: {
    oportunidade: number;
    urgencia: number;
    final: number;
  };
  classificacao?: {
    nivel: string;
    cor: string;
    emoji: string;
    acao: string;
  };
  recomendacoes?: string[];
  urgencia_extra?: string;
  situacao_processual?: string;
  analise_prescricao?: string;
}

interface FiltrosProduto {
  uf?: string[];
  valor_minimo?: number;
  valor_maximo?: number;
  limit: number;
  offset: number;
}

interface ProdutosDashboardProps {
  onSelectCNPJ?: (cnpj: string) => void;
}

const ProdutosDashboard: React.FC<ProdutosDashboardProps> = ({ onSelectCNPJ }) => {
  const [loading, setLoading] = useState(false);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [totalBase, setTotalBase] = useState(0);
  const [produtoAtivo, setProdutoAtivo] = useState<string | null>(null);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [totalEmpresas, setTotalEmpresas] = useState(0);
  const [filtros, setFiltros] = useState<FiltrosProduto>({ limit: 50, offset: 0 });
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState<Empresa | null>(null);

  const carregarDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_SERVIDOR2.PRODUTOS_DASHBOARD);
      setProdutos(response.data.produtos || []);
      setTotalBase(response.data.total_base || 0);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      message.error('Erro ao carregar dashboard de produtos');
    } finally {
      setLoading(false);
    }
  };

  const carregarProduto = async (produtoId: string) => {
    setLoadingEmpresas(true);
    setProdutoAtivo(produtoId);
    
    try {
      const endpoints: Record<string, string> = {
        'novos': API_SERVIDOR2.PRODUTO_RADAR,
        'risco': API_SERVIDOR2.PRODUTO_PRE_AJUIZAMENTO,
        'execucao': API_SERVIDOR2.PRODUTO_AJUIZADOS,
        'antigas': API_SERVIDOR2.PRODUTO_PRESCRICAO,
      };
      
      const response = await axios.post(endpoints[produtoId], filtros);
      setEmpresas(response.data.empresas || []);
      setTotalEmpresas(response.data.total || 0);
      message.success(`${response.data.empresas?.length || 0} empresas carregadas`);
    } catch (error) {
      console.error('Erro ao carregar produto:', error);
      message.error('Erro ao carregar empresas do produto');
    } finally {
      setLoadingEmpresas(false);
    }
  };

  useEffect(() => {
    carregarDashboard();
  }, []);

  useEffect(() => {
    if (produtoAtivo) {
      carregarProduto(produtoAtivo);
    }
  }, [filtros.offset]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCNPJ = (cnpj: string) => {
    if (!cnpj) return '';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    if (score >= 40) return '#fa8c16';
    return '#ff4d4f';
  };

  const getProdutoIcon = (id: string) => {
    switch (id) {
      case 'novos': return <RadarChartOutlined />;
      case 'risco': return <WarningOutlined />;
      case 'execucao': return <ThunderboltOutlined />;
      case 'antigas': return <ClockCircleOutlined />;
      default: return <FileTextOutlined />;
    }
  };

  const getProdutoGradient = (cor: string) => {
    switch (cor) {
      case 'blue': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'orange': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'red': return 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)';
      case 'green': return 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  };

  const columns = [
    {
      title: 'Score',
      key: 'score',
      width: 80,
      render: (_: any, record: Empresa) => {
        const score = record.score?.final || record.score_risco || 0;
        return (
          <Tooltip title={`Oportunidade: ${record.score?.oportunidade || '-'} | Urgência: ${record.score?.urgencia || '-'}`}>
            <Progress
              type="circle"
              percent={score}
              width={45}
              strokeColor={getScoreColor(score)}
              format={(p) => p}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj_completo',
      key: 'cnpj',
      width: 160,
      render: (text: string) => (
        <Text style={{ fontFamily: 'monospace' }}>{formatCNPJ(text)}</Text>
      ),
    },
    {
      title: 'Empresa',
      dataIndex: 'nome_devedor',
      key: 'nome',
      ellipsis: true,
      render: (text: string, record: Empresa) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.classificacao && (
            <Tag color={record.classificacao.cor}>
              {record.classificacao.emoji} {record.classificacao.acao}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Valor Total',
      dataIndex: 'valor_total',
      key: 'valor',
      width: 140,
      render: (value: number) => (
        <Text strong style={{ color: '#1890ff' }}>{formatCurrency(value)}</Text>
      ),
    },
    {
      title: 'Dívidas',
      key: 'dividas',
      width: 110,
      render: (_: any, record: Empresa) => (
        <Space direction="vertical" size={0}>
          <Text>{record.total_dividas} total</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.qtd_ajuizadas} ajuiz / {record.qtd_nao_ajuizadas} não
          </Text>
        </Space>
      ),
    },
    {
      title: 'Tipos',
      key: 'tipos',
      width: 130,
      render: (_: any, record: Empresa) => (
        <Space size={2} wrap>
          {record.tem_fgts && <Tag color="blue">FGTS</Tag>}
          {record.tem_prev && <Tag color="purple">PREV</Tag>}
          {record.tem_sida && <Tag color="cyan">SIDA</Tag>}
        </Space>
      ),
    },
    {
      title: 'Tempo',
      key: 'tempo',
      width: 80,
      render: (_: any, record: Empresa) => (
        <Text type={record.tempo_divida_mais_antiga_anos > 5 ? 'danger' : 'secondary'}>
          {record.tempo_divida_mais_antiga_anos?.toFixed(1)}a
        </Text>
      ),
    },
    {
      title: 'UF',
      dataIndex: 'uf',
      key: 'uf',
      width: 50,
    },
    {
      title: 'Info Extra',
      key: 'extra',
      width: 180,
      render: (_: any, record: Empresa) => (
        <Space direction="vertical" size={0}>
          {record.urgencia_extra && (
            <Text type="danger" style={{ fontSize: 11 }}>{record.urgencia_extra}</Text>
          )}
          {record.situacao_processual && (
            <Text type="secondary" style={{ fontSize: 11 }}>{record.situacao_processual}</Text>
          )}
          {record.analise_prescricao && (
            <Text type="warning" style={{ fontSize: 11 }}>{record.analise_prescricao}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 120,
      render: (_: any, record: Empresa) => (
        <Space size={4}>
          <Tooltip title="Ver resumo">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedEmpresa(record);
                setDetailsVisible(true);
              }}
            />
          </Tooltip>
          {onSelectCNPJ && (
            <Tooltip title="Abrir página completa">
              <Button
                type="primary"
                size="small"
                onClick={() => onSelectCNPJ(record.cnpj_completo || record.cnpj_basico)}
              >
                Abrir
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const renderProdutoCards = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      {produtos.map((produto) => (
        <Col xs={24} sm={12} lg={6} key={produto.id}>
          <Card
            hoverable
            onClick={() => carregarProduto(produto.id)}
            style={{
              background: produtoAtivo === produto.id 
                ? getProdutoGradient(produto.cor)
                : '#fff',
              borderRadius: 12,
              border: produtoAtivo === produto.id ? 'none' : '1px solid #f0f0f0',
              transition: 'all 0.3s',
            }}
            bodyStyle={{ padding: 20 }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <span style={{ 
                  fontSize: 28,
                  color: produtoAtivo === produto.id ? '#fff' : produto.cor === 'blue' ? '#667eea' : produto.cor === 'orange' ? '#f5576c' : produto.cor === 'red' ? '#ff416c' : '#11998e'
                }}>
                  {getProdutoIcon(produto.id)}
                </span>
                <div>
                  <Text 
                    strong 
                    style={{ 
                      fontSize: 16,
                      color: produtoAtivo === produto.id ? '#fff' : 'inherit'
                    }}
                  >
                    {produto.nome.replace(/[🔵🟠🔴⏰]/g, '').trim()}
                  </Text>
                  <br />
                  <Text 
                    type="secondary"
                    style={{ 
                      fontSize: 12,
                      color: produtoAtivo === produto.id ? 'rgba(255,255,255,0.85)' : 'inherit'
                    }}
                  >
                    {produto.descricao}
                  </Text>
                </div>
              </Space>
              
              <Statistic
                value={produto.total}
                valueStyle={{ 
                  color: produtoAtivo === produto.id ? '#fff' : '#1890ff',
                  fontSize: 28 
                }}
                suffix={<Text style={{ 
                  fontSize: 12, 
                  color: produtoAtivo === produto.id ? 'rgba(255,255,255,0.85)' : '#999'
                }}>empresas</Text>}
              />
              
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderFiltros = () => (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={16} align="middle">
        <Col span={6}>
          <Select
            mode="multiple"
            placeholder="Filtrar por UF"
            style={{ width: '100%' }}
            onChange={(value) => setFiltros({ ...filtros, uf: value, offset: 0 })}
            options={[
              { value: 'SP', label: 'São Paulo' },
              { value: 'RJ', label: 'Rio de Janeiro' },
              { value: 'MG', label: 'Minas Gerais' },
              { value: 'RS', label: 'Rio Grande do Sul' },
              { value: 'PR', label: 'Paraná' },
              { value: 'SC', label: 'Santa Catarina' },
              { value: 'BA', label: 'Bahia' },
              { value: 'GO', label: 'Goiás' },
              { value: 'DF', label: 'Distrito Federal' },
            ]}
          />
        </Col>
        <Col span={4}>
          <Input
            placeholder="Valor mínimo"
            prefix="R$"
            type="number"
            onChange={(e) => setFiltros({ 
              ...filtros, 
              valor_minimo: e.target.value ? Number(e.target.value) : undefined,
              offset: 0 
            })}
          />
        </Col>
        <Col span={4}>
          <Input
            placeholder="Valor máximo"
            prefix="R$"
            type="number"
            onChange={(e) => setFiltros({ 
              ...filtros, 
              valor_maximo: e.target.value ? Number(e.target.value) : undefined,
              offset: 0 
            })}
          />
        </Col>
        <Col span={6}>
          <Space>
            <Button 
              type="primary" 
              icon={<SearchOutlined />}
              onClick={() => produtoAtivo && carregarProduto(produtoAtivo)}
              loading={loadingEmpresas}
            >
              Filtrar
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => {
                setFiltros({ limit: 50, offset: 0 });
                if (produtoAtivo) carregarProduto(produtoAtivo);
              }}
            >
              Limpar
            </Button>
          </Space>
        </Col>
        <Col span={4} style={{ textAlign: 'right' }}>
          <Text type="secondary">
            Total: <strong>{totalEmpresas.toLocaleString()}</strong>
          </Text>
        </Col>
      </Row>
    </Card>
  );

  const renderDetalhesModal = () => (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Análise Detalhada</span>
        </Space>
      }
      open={detailsVisible}
      onCancel={() => setDetailsVisible(false)}
      width={800}
      footer={[
        <Button key="close" onClick={() => setDetailsVisible(false)}>
          Fechar
        </Button>,
        <Button key="action" type="primary" icon={<RocketOutlined />}>
          Adicionar à Carteira
        </Button>,
      ]}
    >
      {selectedEmpresa && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="CNPJ" span={1}>
              <Text copyable style={{ fontFamily: 'monospace' }}>
                {formatCNPJ(selectedEmpresa.cnpj_completo)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Score Final" span={1}>
              <Progress
                percent={selectedEmpresa.score?.final || selectedEmpresa.score_risco}
                strokeColor={getScoreColor(selectedEmpresa.score?.final || selectedEmpresa.score_risco)}
                style={{ width: 120 }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Razão Social" span={2}>
              {selectedEmpresa.nome_devedor}
            </Descriptions.Item>
            <Descriptions.Item label="Valor Total">
              <Text strong style={{ color: '#1890ff', fontSize: 18 }}>
                {formatCurrency(selectedEmpresa.valor_total)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Total de Dívidas">
              {selectedEmpresa.total_dividas} 
              <Text type="secondary"> ({selectedEmpresa.qtd_ajuizadas} ajuizadas)</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Tipos de Dívida">
              <Space>
                {selectedEmpresa.tem_fgts && <Tag color="blue">FGTS</Tag>}
                {selectedEmpresa.tem_prev && <Tag color="purple">PREV</Tag>}
                {selectedEmpresa.tem_sida && <Tag color="cyan">SIDA</Tag>}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="UF">
              {selectedEmpresa.uf}
            </Descriptions.Item>
            <Descriptions.Item label="Tempo (dívida antiga)">
              <Text type={selectedEmpresa.tempo_divida_mais_antiga_anos > 5 ? 'danger' : 'secondary'}>
                {selectedEmpresa.tempo_divida_mais_antiga_anos?.toFixed(1)} anos
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Classificação">
              {selectedEmpresa.classificacao && (
                <Tag color={selectedEmpresa.classificacao.cor}>
                  {selectedEmpresa.classificacao.emoji} {selectedEmpresa.classificacao.nivel}
                </Tag>
              )}
            </Descriptions.Item>
          </Descriptions>

          {selectedEmpresa.score && (
            <Card title="Scores Calculados" size="small">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Oportunidade"
                    value={selectedEmpresa.score.oportunidade}
                    suffix="/100"
                    valueStyle={{ color: getScoreColor(selectedEmpresa.score.oportunidade) }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Urgência"
                    value={selectedEmpresa.score.urgencia}
                    suffix="/100"
                    valueStyle={{ color: getScoreColor(selectedEmpresa.score.urgencia) }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Final"
                    value={selectedEmpresa.score.final}
                    suffix="/100"
                    valueStyle={{ color: getScoreColor(selectedEmpresa.score.final) }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {selectedEmpresa.recomendacoes && selectedEmpresa.recomendacoes.length > 0 && (
            <Card title="Recomendações" size="small">
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {selectedEmpresa.recomendacoes.map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </Card>
          )}

          {(selectedEmpresa.urgencia_extra || selectedEmpresa.situacao_processual || selectedEmpresa.analise_prescricao) && (
            <Card title="Alertas Especiais" size="small" style={{ background: '#fff7e6' }}>
              {selectedEmpresa.urgencia_extra && (
                <Paragraph type="danger">{selectedEmpresa.urgencia_extra}</Paragraph>
              )}
              {selectedEmpresa.situacao_processual && (
                <Paragraph>{selectedEmpresa.situacao_processual}</Paragraph>
              )}
              {selectedEmpresa.analise_prescricao && (
                <Paragraph type="warning">{selectedEmpresa.analise_prescricao}</Paragraph>
              )}
            </Card>
          )}
        </Space>
      )}
    </Modal>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>
          <span style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Produtos PGFN
          </span>
          <Text style={{ marginLeft: 12, fontSize: 16, fontWeight: 400 }}>
            4 Algoritmos de Prospecção Inteligente
          </Text>
        </Title>
        <Text type="secondary">
          Base total: <strong>{totalBase.toLocaleString()}</strong> empresas com dívidas ativas
        </Text>
      </div>

      <Spin spinning={loading}>
        {renderProdutoCards()}
      </Spin>

      {produtoAtivo && (
        <>
          {renderFiltros()}
          
          <Card>
            <Table
              columns={columns}
              dataSource={empresas}
              rowKey="cnpj_completo"
              loading={loadingEmpresas}
              pagination={{
                total: totalEmpresas,
                pageSize: filtros.limit,
                current: Math.floor(filtros.offset / filtros.limit) + 1,
                onChange: (page) => setFiltros({ ...filtros, offset: (page - 1) * filtros.limit }),
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total.toLocaleString()}`,
              }}
              scroll={{ x: 1400 }}
            />
          </Card>
        </>
      )}

      {renderDetalhesModal()}
    </div>
  );
};

export default ProdutosDashboard;
